import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { 
  TrendingUp, 
  Users, 
  Target, 
  Activity,
  Euro,
  ArrowUpRight,
  Clock,
  BarChart3,
  PieChart,
  Package,
  FileText,
  Award,
  Percent
} from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart as RechartsPie,
  Pie,
  Cell
} from 'recharts';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';

const EP3D_LOGO = "https://customer-assets.emergentagent.com/job_sales-force-hub-3/artifacts/8yq7y2ex_Logotipo_eng3d_2-removebg-preview%20%281%29.gif";

const STAGE_COLORS = {
  lead: '#94a3b8',
  qualificado: '#3b82f6',
  proposta: '#f59e0b',
  negociacao: '#8b5cf6',
  ganho: '#10b981',
  perdido: '#ef4444'
};

const STAGE_NAMES = {
  lead: 'Lead',
  qualificado: 'Qualificado',
  proposta: 'Proposta',
  negociacao: 'Negociação',
  ganho: 'Ganho',
  perdido: 'Perdido'
};

export default function DashboardPage() {
  const { api, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentActivities, setRecentActivities] = useState([]);
  const [topClients, setTopClients] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [salesSummary, setSalesSummary] = useState(null);
  const [commission, setCommission] = useState(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const [statsRes, activitiesRes, topClientsRes, topProductsRes, salesRes, commissionRes] = await Promise.all([
        api().get('/dashboard/stats'),
        api().get('/activities?limit=5'),
        api().get('/analytics/top-clients?limit=5'),
        api().get('/analytics/top-products?limit=5'),
        api().get('/analytics/sales-summary'),
        api().get('/analytics/my-commission')
      ]);
      setStats(statsRes.data);
      setRecentActivities(activitiesRes.data);
      setTopClients(topClientsRes.data);
      setTopProducts(topProductsRes.data);
      setSalesSummary(salesRes.data);
      setCommission(commissionRes.data);
    } catch (error) {
      console.error('Failed to load dashboard', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const getPipelineChartData = () => {
    if (!stats?.pipeline_stats) return [];
    return Object.entries(stats.pipeline_stats).map(([stage, data]) => ({
      name: STAGE_NAMES[stage] || stage,
      value: data.value,
      count: data.count,
      fill: STAGE_COLORS[stage] || '#94a3b8'
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
      </div>
    );
  }

  const pipelineData = getPipelineChartData();

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header with Logo */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <img src={EP3D_LOGO} alt="EP3D" className="h-12 w-auto" />
          <div>
            <h1 className="text-xl font-bold font-[Manrope] text-amber-500">VENDAS</h1>
            <p className="text-sm text-muted-foreground capitalize">
              {format(new Date(), "EEEE, d 'de' MMMM", { locale: pt })}
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={loadDashboard} data-testid="refresh-btn">
          Atualizar
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Card className="bg-gradient-to-br from-slate-800 to-slate-900 text-white border-0" data-testid="kpi-total-revenue">
          <CardContent className="p-3 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
              <div className="min-w-0">
                <p className="text-slate-300 text-xs sm:text-sm font-medium">Volume Vendas</p>
                <p className="text-xl sm:text-3xl font-extrabold font-[Manrope] mt-1 sm:mt-2 tabular-nums text-amber-400 truncate">
                  {formatCurrency(salesSummary?.total_revenue)}
                </p>
                <p className="text-slate-300 text-xs sm:text-sm mt-1 sm:mt-2">
                  {salesSummary?.total_orders || 0} enc.
                </p>
              </div>
              <div className="p-2 sm:p-3 bg-amber-500/20 rounded-lg self-start">
                <Euro className="h-4 w-4 sm:h-6 sm:w-6 text-amber-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-600 to-emerald-700 text-white border-0" data-testid="kpi-clients">
          <CardContent className="p-3 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
              <div className="min-w-0">
                <p className="text-emerald-100 text-xs sm:text-sm font-medium">Clientes</p>
                <p className="text-xl sm:text-3xl font-extrabold font-[Manrope] mt-1 sm:mt-2 tabular-nums">
                  {salesSummary?.total_clients || 0}
                </p>
                <div className="flex items-center gap-1 mt-1 sm:mt-2 text-emerald-100">
                  <ArrowUpRight className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="text-xs sm:text-sm">carteira</span>
                </div>
              </div>
              <div className="p-2 sm:p-3 bg-white/10 rounded-lg self-start">
                <Users className="h-4 w-4 sm:h-6 sm:w-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow" data-testid="kpi-pending">
          <CardContent className="p-3 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
              <div className="min-w-0">
                <p className="text-muted-foreground text-xs sm:text-sm font-medium">Pendentes</p>
                <p className="text-xl sm:text-3xl font-extrabold font-[Manrope] mt-1 sm:mt-2 tabular-nums text-slate-900">
                  {salesSummary?.pending_orders || 0}
                </p>
                <p className="text-muted-foreground text-xs sm:text-sm mt-1 sm:mt-2">a processar</p>
              </div>
              <div className="p-2 sm:p-3 bg-amber-100 rounded-lg self-start">
                <Clock className="h-4 w-4 sm:h-6 sm:w-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Commission Card */}
        <Card className="hover:shadow-md transition-shadow" data-testid="kpi-commission">
          <CardContent className="p-3 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
              <div className="min-w-0">
                <p className="text-muted-foreground text-xs sm:text-sm font-medium">Comissão</p>
                <p className="text-xl sm:text-3xl font-extrabold font-[Manrope] mt-1 sm:mt-2 tabular-nums text-emerald-600 truncate">
                  {formatCurrency(commission?.commission_earned)}
                </p>
                <p className="text-muted-foreground text-xs sm:text-sm mt-1 sm:mt-2">
                  <Badge variant="outline" className="text-xs">
                    <Percent className="h-2 w-2 sm:h-3 sm:w-3 mr-1" />
                    {commission?.commission_rate || 0}%
                  </Badge>
                </p>
              </div>
              <div className="p-2 sm:p-3 bg-emerald-100 rounded-lg self-start">
                <Award className="h-4 w-4 sm:h-6 sm:w-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Analytics Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Top Clients */}
        <Card data-testid="top-clients-widget">
          <CardHeader className="pb-2 px-3 sm:px-6">
            <CardTitle className="text-base sm:text-lg font-semibold font-[Manrope] flex items-center gap-2">
              <Award className="h-4 w-4 sm:h-5 sm:w-5 text-amber-500" />
              Melhores Clientes
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-6">
            {topClients.length > 0 ? (
              <div className="space-y-2 sm:space-y-3">
                {topClients.map((client, index) => (
                  <div key={client.client_id} className="flex items-center justify-between p-2 sm:p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                      <div className={`
                        w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold flex-shrink-0
                        ${index === 0 ? 'bg-amber-100 text-amber-700' : 
                          index === 1 ? 'bg-slate-200 text-slate-700' :
                          index === 2 ? 'bg-orange-100 text-orange-700' :
                          'bg-slate-100 text-slate-600'}
                      `}>
                        {index + 1}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-sm sm:text-base truncate">{client.client_name}</p>
                        <p className="text-xs text-muted-foreground">{client.total_orders} enc.</p>
                      </div>
                    </div>
                    <span className="font-bold tabular-nums text-emerald-600 text-sm sm:text-base flex-shrink-0">
                      {formatCurrency(client.total_value)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 sm:py-8 text-muted-foreground">
                <Users className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm">Nenhuma venda registada</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Products */}
        <Card data-testid="top-products-widget">
          <CardHeader className="pb-2 px-3 sm:px-6">
            <CardTitle className="text-base sm:text-lg font-semibold font-[Manrope] flex items-center gap-2">
              <Package className="h-4 w-4 sm:h-5 sm:w-5 text-amber-500" />
              Produtos Mais Vendidos
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-6">
            {topProducts.length > 0 ? (
              <div className="space-y-2 sm:space-y-3">
                {topProducts.map((product, index) => (
                  <div key={product.product_id} className="flex items-center justify-between p-2 sm:p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                      <div className={`
                        w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold flex-shrink-0
                        ${index === 0 ? 'bg-amber-100 text-amber-700' : 
                          index === 1 ? 'bg-slate-200 text-slate-700' :
                          index === 2 ? 'bg-orange-100 text-orange-700' :
                          'bg-slate-100 text-slate-600'}
                      `}>
                        {index + 1}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-sm sm:text-base truncate">{product.product_name}</p>
                        <p className="text-xs text-muted-foreground">{product.total_quantity} un.</p>
                      </div>
                    </div>
                    <span className="font-bold tabular-nums text-emerald-600 text-sm sm:text-base flex-shrink-0">
                      {formatCurrency(product.total_value)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 sm:py-8 text-muted-foreground">
                <Package className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm">Nenhum produto vendido</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pipeline Chart */}
        <Card className="lg:col-span-2" data-testid="pipeline-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold font-[Manrope] flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-amber-500" />
              Pipeline por Etapa
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={pipelineData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                  <XAxis 
                    type="number" 
                    tickFormatter={(value) => formatCurrency(value)}
                    fontSize={12}
                  />
                  <YAxis 
                    type="category" 
                    dataKey="name" 
                    width={100}
                    fontSize={12}
                  />
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    labelFormatter={(label) => `Etapa: ${label}`}
                  />
                  <Bar 
                    dataKey="value" 
                    radius={[0, 4, 4, 0]}
                  >
                    {pipelineData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Recent Activities */}
        <Card data-testid="activities-widget">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold font-[Manrope] flex items-center gap-2">
              <Activity className="h-5 w-5 text-amber-500" />
              Atividade Recente
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentActivities.length > 0 ? (
              <div className="space-y-4">
                {recentActivities.map((activity) => (
                  <div key={activity.id} className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${
                      activity.activity_type === 'chamada' ? 'bg-blue-100' :
                      activity.activity_type === 'visita' ? 'bg-emerald-100' :
                      activity.activity_type === 'email' ? 'bg-purple-100' :
                      'bg-slate-100'
                    }`}>
                      <Activity className={`h-4 w-4 ${
                        activity.activity_type === 'chamada' ? 'text-blue-600' :
                        activity.activity_type === 'visita' ? 'text-emerald-600' :
                        activity.activity_type === 'email' ? 'text-purple-600' :
                        'text-slate-600'
                      }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{activity.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {activity.contact_name && `${activity.contact_name} • `}
                        {format(new Date(activity.created_at), "d MMM 'às' HH:mm", { locale: pt })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Activity className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>Nenhuma atividade registada</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Goals Section */}
      <Card data-testid="goals-widget">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold font-[Manrope] flex items-center gap-2">
            <Target className="h-5 w-5 text-amber-500" />
            Objetivos
          </CardTitle>
        </CardHeader>
        <CardContent>
          {stats?.goals && stats.goals.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {stats.goals.slice(0, 3).map((goal) => {
                const progress = (goal.current_value / goal.target_value) * 100;
                return (
                  <div key={goal.id} className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">{goal.title}</span>
                      <Badge variant={progress >= 100 ? 'default' : 'secondary'} className={progress >= 100 ? 'bg-emerald-600' : ''}>
                        {progress >= 100 ? 'Atingido!' : `${Math.round(progress)}%`}
                      </Badge>
                    </div>
                    <Progress value={Math.min(progress, 100)} className="h-2" />
                    <div className="flex justify-between text-xs text-muted-foreground mt-2 tabular-nums">
                      <span>{formatCurrency(goal.current_value)}</span>
                      <span>{formatCurrency(goal.target_value)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Target className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>Nenhum objetivo definido</p>
              <Button variant="link" className="mt-2" onClick={() => window.location.href = '/goals'}>
                Criar objetivo
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
